import React, { useEffect, useRef } from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import nth from 'lodash/nth';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { color, font } from 'src/shared/styles/variables';
import { useTranslation } from 'react-i18next';
import i18n from 'src/config/i18n';

import {
    Answer,
    FieldType,
    MultiSelectQuestion as IMultiSelectQuestion,
    Question,
} from '../models/assessments';
import { MultiSelectQuestion } from './MultiSelectQuestion';
import { SingleSelectQuestion } from './SingleSelectQuestion';
import { NumberInputQuestion } from './NumberInputQuestion';
import {
    Button,
    ButtonGroup,
    CalloutInline,
    Form,
    HeadingText40,
    useForm,
} from '@modernhealth/vital-components';
import { useAnalytics } from '@modernhealth/web-analytics';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

const maxQuestionWidth = 500;
const PeriodHeader = styled.h3(
    {
        color: color.blueBayoux,
        margin: '1rem',
        maxWidth: maxQuestionWidth,
    },
    font.roman(font.size.medium),
);

const CalloutContainer = styled.div({
    marginBottom: 24,
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    maxWidth: maxQuestionWidth,
});

const QuestionContainer = styled.div({
    position: 'relative',
    display: 'inline-flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 15,
});

const Underline = styled.span({
    textDecoration: 'underline',
});

const fadeIn = keyframes`{
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
     }
}`;

const schema = Yup.object().when('$shouldValidateSicknessDays', {
    is: true,
    then: schema =>
        schema.shape({
            currentField: Yup.number()
                .min(0, () => i18n.t('assessments:employment.number_field.bounds_error'))
                .max(360, () => i18n.t('assessments:employment.number_field.bounds_error'))
                .typeError(() => i18n.t('assessments:employment.number_field.bounds_error')),
        }),
    otherwise: schema => schema.shape({}),
});

type AssessmentPageData = {} | { currentField: number };

interface Props {
    assessmentName?: string;
    assessmentFlowUuid: string;
    question: IMultiSelectQuestion;
    headerText?: string | null;
    answer: Answer;
    onAnswerChange: (questionId: string, value: number | null | string | string[]) => void;
    onAnswerSubmit: () => void;
    customHeader?: (question: IMultiSelectQuestion) => React.ReactNode;
    shouldDisplayBackButton: boolean;
    handleGoBack: () => void;
    submitInProgress: boolean;
    isContinueButtonDisabled: boolean;
    lastStep: boolean;
    handleQuestionSkip: (question: string) => void;
}

export const AssessmentQuestion: React.FC<Props> = ({
    assessmentName,
    assessmentFlowUuid,
    question,
    answer,
    onAnswerChange,
    onAnswerSubmit,
    headerText,
    children,
    customHeader,
    shouldDisplayBackButton,
    handleGoBack,
    submitInProgress,
    isContinueButtonDisabled,
    lastStep,
    handleQuestionSkip,
}) => {
    const { t } = useTranslation();
    const analytics = useAnalytics();

    const { showNewEmploymentQuestions: numberInputQuestionFeatureFlag } = useFlags();

    const currentAssessmentId = question?.questionKey;

    const currentAssessmentGroup = currentAssessmentId.startsWith('who5')
        ? currentAssessmentId
        : currentAssessmentId.replace(/\d+(remainder)?/g, ''); // Converts 'phq2', 'phq9', or 'phq9remainder into just 'phq', leaves 'who5' as is

    const sicknessString = 'ick'; // There's a bug where one of the question keys should be 'sickness' but is 'stickness' and it's all throughout the backend and database so now we just check for 'ick'
    const isSicknessQuestion = question.questionKey.includes(sicknessString); // TODO: Move this check to the backend via question.type === FieldType.NumberInput

    const showNumberInputQuestion =
        (question.type === FieldType.NumberInput || isSicknessQuestion) &&
        numberInputQuestionFeatureFlag;
    const shouldValidateSicknessDays = showNumberInputQuestion;
    const formValidationContext = useRef({ shouldValidateSicknessDays });
    Object.assign(formValidationContext.current, { shouldValidateSicknessDays });

    const formDefinition = useForm<AssessmentPageData>({
        resolver: yupResolver(schema),
        context: formValidationContext.current,
        mode: 'onChange',
    });

    const formFieldName = 'currentField';

    useEffect(() => {
        if (!question) {
            return;
        }

        const eventName = `Assessment ${currentAssessmentGroup}`;
        analytics.trackSinglePageFlow(eventName, {
            flow: 'Assessment',
            assessment_flow_id: assessmentFlowUuid,
            assessment_id: assessmentName,
        });
    }, [currentAssessmentGroup, analytics, assessmentFlowUuid, assessmentName, question]);

    if (!question) {
        return null;
    }

    const shouldDisplayHeaderText = headerText != null && headerText !== '';
    const headerTextPartToUnderline =
        shouldDisplayHeaderText &&
        nth(headerText.match(/<underline>([\s\S]*)(?=<\/underline>)/), 1);

    const renderQuestionComponent = () => {
        // Not sure where in the DOM this should be set, but it should be set somewhere 🤣
        const questionLabelId = `${question.questionKey}--label`;

        if (question.type === FieldType.MultiSelect) {
            return (
                <MultiSelectQuestion
                    options={(question as Question).responseType}
                    onRateChange={(value: string[]) => onAnswerChange(question.questionKey, value)}
                    currentRate={(answer as string[]) ?? []}
                />
            );
        }
        if (question.type === FieldType.NumberInput || isSicknessQuestion) {
            const currentAnswer = typeof answer === 'number' ? answer : undefined;
            return (
                <NumberInputQuestion
                    labelledBy={questionLabelId}
                    placeholderText={t('assessments:employment.number_field.placeholder')}
                    validationText={t('assessments:employment.number_field.validation')}
                    currentAnswer={currentAnswer}
                    onAnswerChange={(value: number) => onAnswerChange(question.questionKey, value)}
                    formFieldName={formFieldName}
                />
            );
        }
        if (question.type === FieldType.SingleSelect) {
            return (
                <SingleSelectQuestion
                    options={(question as Question).responseType}
                    onRateChange={(value: string) => onAnswerChange(question.questionKey, value)}
                    currentRate={answer as string}
                />
            );
        }
        return (
            <SingleSelectQuestion
                options={(question as Question).responseType}
                onRateChange={(value: string) => onAnswerChange(question.questionKey, value)}
                currentRate={answer as string}
            />
        );
    };

    const PeriodHeaderComponent: React.FC = () => {
        if (headerTextPartToUnderline) {
            return (
                <PeriodHeader>
                    {headerText.substring(0, headerText.indexOf('<underline>'))}
                    <Underline>{headerTextPartToUnderline}</Underline>
                    {headerText.substring(
                        headerText.indexOf('</underline>') + 12,
                        headerText.length,
                    )}
                </PeriodHeader>
            );
        }
        return <PeriodHeader>{headerText}</PeriodHeader>;
    };

    // This is a function so its value (passed by reference) below is always up to date
    const isResponseValid = () => {
        if (showNumberInputQuestion) {
            return formDefinition.formState.isValid;
        }
        return !isContinueButtonDisabled; // TODO: Can this be included as part of the schema so we can eliminate this function and just use built-in form validation?
    };

    const navigationButtonWidth = 241;

    return (
        <>
            {customHeader ? (
                customHeader(question)
            ) : (
                <>
                    {!shouldDisplayHeaderText ? null : <PeriodHeaderComponent />}

                    <HeadingText40
                        as="h2"
                        centered
                        css={{ marginBottom: 30, maxWidth: maxQuestionWidth }}
                    >
                        {question.questionText}
                    </HeadingText40>
                </>
            )}
            {question.calloutText && (
                <CalloutContainer>
                    <CalloutInline variant="default" css={{ textAlign: 'left' }}>
                        {question.calloutText}
                    </CalloutInline>
                </CalloutContainer>
            )}

            <Form
                definition={formDefinition}
                css={{ gridGap: 0 }}
                onSubmit={() => {
                    // We must run this check here (as opposed to just trusting the form API to handle this
                    // because we need isContinueButtonDisabled to be part of this
                    if (!isResponseValid()) {
                        return;
                    }

                    onAnswerSubmit();
                }}
            >
                <QuestionContainer>
                    {numberInputQuestionFeatureFlag ? (
                        renderQuestionComponent()
                    ) : (
                        <>
                            {question.type === FieldType.MultiSelect ? (
                                <MultiSelectQuestion
                                    options={(question as Question).responseType}
                                    onRateChange={(value: string[]) =>
                                        onAnswerChange(question.questionKey, value)
                                    }
                                    currentRate={(answer as string[]) ?? []}
                                />
                            ) : (
                                <SingleSelectQuestion
                                    options={(question as Question).responseType}
                                    onRateChange={(value: string) =>
                                        onAnswerChange(question.questionKey, value)
                                    }
                                    currentRate={answer as string}
                                />
                            )}
                        </>
                    )}
                    {children}
                </QuestionContainer>
                <ButtonGroup
                    css={{ marginTop: 40, flex: 'none', display: 'flex', justifyContent: 'center' }}
                >
                    {shouldDisplayBackButton && (
                        <Button
                            variant="secondary"
                            size="large"
                            onClick={handleGoBack}
                            css={{
                                minWidth: navigationButtonWidth,
                                maxWidth: navigationButtonWidth,
                            }}
                        >
                            {t('common.back')}
                        </Button>
                    )}
                    <Button
                        type="submit"
                        size="large"
                        busy={submitInProgress}
                        disabled={!isResponseValid()}
                        css={{
                            minWidth: navigationButtonWidth,
                            maxWidth: navigationButtonWidth,
                        }}
                    >
                        {t(
                            question.type !== FieldType.MultiSelect && lastStep
                                ? 'common.submit'
                                : 'common.continue',
                        )}
                    </Button>
                </ButtonGroup>
                {question.optional || question.type === FieldType.MultiSelect ? (
                    <Button
                        onClick={() => handleQuestionSkip(question.questionKey)}
                        css={{
                            marginTop: 5,
                            paddingLeft: 0,
                            paddingRight: 0,
                            animation: `${fadeIn} 400ms ease`,
                            transition: 'background-color 200ms ease',
                        }}
                        variant="flat"
                    >
                        {t(
                            question.type === FieldType.MultiSelect
                                ? 'common.none_of_these'
                                : 'who5Survey.skipQuestion',
                        )}
                    </Button>
                ) : null}
            </Form>
        </>
    );
};
